import { useEffect, useState } from 'react';
import { RefreshCw, AlertCircle } from 'lucide-react';
import OpenAppLink from './OpenAppLink';
import {
  fetchDashboardSummary,
  fetchIntegrationStatus,
  retryIntegration,
  getStatusColor,
  getStatusLabel,
  integrationLabel,
  normalizeIntegrationStatusValue,
  INTEGRATION_KEYS,
  type IntegrationStatus,
} from '../lib/api';

function StatusBadge({ status }: { status: IntegrationStatus['status'] }) {
  return (
    <span className="flex items-center gap-1.5 text-xs font-medium">
      <span className={`h-2 w-2 rounded-full ${getStatusColor(status)}`} />
      {getStatusLabel(status)}
    </span>
  );
}

function StatusWidgets() {
  const [integrations, setIntegrations] = useState<IntegrationStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadError(null);
      try {
        const summary = await fetchDashboardSummary();
        if (!cancelled) {
          if (summary.ok && summary.data?.integrations?.length) {
            setIntegrations(summary.data.integrations);
            setLoadError(null);
          } else if (summary.ok && summary.data) {
            const results = await Promise.all(
              INTEGRATION_KEYS.map((name) => fetchIntegrationStatus(name))
            );
            setIntegrations(
              results.map((r, i) => ({
                name: INTEGRATION_KEYS[i],
                status: r.ok
                  ? normalizeIntegrationStatusValue(r.data?.status ?? 'unknown')
                  : 'unknown',
                lastSyncAt: r.data?.last_checked,
                message: r.data?.last_error ?? r.error?.message ?? undefined,
                configured: r.data?.configured,
              }))
            );
            if (results.every((r) => !r.ok)) {
              setLoadError(results[0]?.error?.message ?? 'Could not load integration status');
            }
          } else {
            setIntegrations([]);
            setLoadError(summary.error?.message ?? 'Could not load integration status');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRetry = async (name: string) => {
    setRetrying((prev) => new Set(prev).add(name));
    const result = await retryIntegration(name);
    if (result.ok) {
      setIntegrations((prev) =>
        prev.map((item) =>
          item.name === name
            ? {
                ...item,
                status: normalizeIntegrationStatusValue(result.data?.status ?? item.status),
                message: result.data?.last_error ?? undefined,
              }
            : item
        )
      );
    }
    setRetrying((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-zt-border bg-zt-surface p-6">
        <h3 className="mb-3 font-semibold text-white">Integration health</h3>
        <div className="space-y-3">
          {INTEGRATION_KEYS.slice(0, 5).map((name) => (
            <div key={name} className="h-4 w-full animate-pulse rounded bg-zt-border" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-zt-border bg-zt-surface p-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-semibold text-white">Integration health</h3>
        <span className="text-xs text-zt-muted">{integrations.length} integrations</span>
      </div>

      {integrations.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-zt-muted">
          <AlertCircle size={16} />
          {loadError ?? 'No integration status available yet.'}
        </div>
      ) : (
        <div className="space-y-2">
          {integrations.map((item) => (
            <div
              key={item.name}
              className="flex items-center justify-between rounded-lg border border-zt-border bg-zt-bg px-3 py-2"
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-slate-200">
                  {integrationLabel(item.name)}
                </div>
                {item.message && (
                  <div className="truncate text-xs text-zt-muted">{item.message}</div>
                )}
                {item.configured && <OpenAppLink integrationKey={item.name} className="mt-1" />}
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={item.status} />
                <button
                  type="button"
                  onClick={() => handleRetry(item.name)}
                  disabled={retrying.has(item.name)}
                  className="rounded p-1 text-zt-muted hover:text-zt-primary disabled:opacity-50"
                  aria-label={`Retry ${integrationLabel(item.name)}`}
                  title={`Retry ${integrationLabel(item.name)}`}
                >
                  <RefreshCw
                    size={16}
                    className={retrying.has(item.name) ? 'animate-spin' : ''}
                  />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default StatusWidgets;
